import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fg } from '@atlaskit/platform-feature-flags';
import { UFOThirdPartySegment } from '@atlaskit/react-ufo/segment';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { CustomUIRenderer, ModalCustomUIRenderer } from './CustomUIRenderer';
import { UIKitRenderer } from './UIKitRenderer';

import {
	makeFrame,
	makeModalThreeLOPromptForCustomUI,
	makeThreeLOPromptForCustomUI,
} from '../../components';
import { ForgeUIExtensionAnalyticsContext } from '../../analytics';
import {
	type ForgeUIRendererProps,
	type ConfluenceCustomUIProps,
	type CloudOrWorkspaceId,
} from './types';
import { isCustomUI } from '../../utils/isCustomUI';
import { PerformanceAnalyticsProviderRollout } from '../../performance-profiling/PerformanceAnalyticsProvider';
import { PerformanceHoldAttributionAnchorRollout } from '../../performance-profiling/PerformanceHoldAttributionAnchor';
import { useExtensionList } from '../../web-client/hooks/useExtensionList';
import { GQLExtensionContextsFilterType } from '../../web-client/graphql/types';
import { Loader } from '../../web-runtime';
import { ExtensionNotInstalled } from './ExtensionNotInstalled';
import { UserAccessProvider } from '../../context/user-access';

type Props = ForgeUIRendererProps & CloudOrWorkspaceId & ConfluenceCustomUIProps;

const getLoadingComponent = (loadingComponent?: React.ReactNode) => {
	if (loadingComponent) {
		// Wrapping loadingComponent in a valid react element (fragment)
		// ReactNode type is not necessarily a valid React element, unable to change this type as different extension points pass in various loadingComponent types
		return <>{loadingComponent}</>;
	}
	return <Loader />;
};

export const ForgeUIRenderer = (props: Props): React.JSX.Element => {
	const {
		accountId,
		bridge,
		client,
		components,
		consentMessage,
		contextIds,
		customBridgeMethods,
		dialogs,
		entryPoint,
		extension: providedExtension,
		extensionData,
		extensionPayload,
		extensionViewData,
		hasModalConsentPrompt,
		height,
		width,
		isIframeHidden,
		isIframeResizable,
		getContextToken,
		loadingComponent,
		locale,
		localId,
		mentionProvider,
		modalExtension,
		modalStyles,
		modalProvider,
		onConsentModalClose,
		onConsentSuccess,
		onConsentInitialization,
		onError,
		onForgeDocUpdated,
		onIframeLoad,
		onInitialRender,
		product,
		timezone,
		getModalWidth,
		onModalExtensionClose,
		onSuccess,
		onAppError,
		viewportDimensions,
		isEmbeddedMacro,
		embeddedParentLocalId,
	} = props;

	let cloudId: string | undefined;
	let workspaceId: string | undefined;

	if ('cloudId' in props) {
		cloudId = props.cloudId;
	} else if ('workspaceId' in props) {
		workspaceId = props.workspaceId;
	}

	const cloudOrWorkspaceId = cloudId ? { cloudId } : ({ workspaceId } as { workspaceId: string });

	// For embedded macros, fetch fresh extension data from XLS instead of using
	// the potentially stale version stored in the ADF. This ensures embedded macros
	// always use the latest deployed version in view mode.
	const shouldRefetchExtension =
		isEmbeddedMacro &&
		fg('platform_refetch_embedded_macro_extension') &&
		!fg('platform_forge_clean_up_embed_ext_properties');
	const extensionType = providedExtension.type;
	const { extensions: freshExtensions, loading: extensionsLoading } = useExtensionList({
		client,
		contextIds,
		...(shouldRefetchExtension && extensionType
			? {
					filter: [
						{
							type: GQLExtensionContextsFilterType.ExtensionType,
							value: [extensionType],
						},
					],
				}
			: {}),
		queryOptions: {
			skip: !shouldRefetchExtension,
		},
	});

	// Use fresh extension if available for embedded macros, otherwise use the prop
	const freshExtension = shouldRefetchExtension
		? freshExtensions?.find((ext) => ext.id === providedExtension.id)
		: undefined;

	// Query has completed when freshExtensions is defined (even if empty array)
	// This distinguishes "not yet loaded" (undefined) from "loaded but not found" (empty array)
	const extensionQueryCompleted = freshExtensions !== undefined;

	// For embedded macros, validate that the extension is actually installed on this tenant
	// If refetch completed and extension was not found, the macro is not installed
	const extensionNotInstalled =
		shouldRefetchExtension &&
		extensionQueryCompleted &&
		!freshExtension &&
		fg('platform_validate_embedded_macro_installation');

	const extension = freshExtension ?? providedExtension;

	const { id: extensionId, environmentId, environmentType } = extension;

	useEffect(() => {
		onInitialRender?.();
	}, [onInitialRender]);

	const coreData = useMemo(
		() => ({
			workspaceId,
			cloudId,
			localId: localId || extensionId,
			environmentId,
			environmentType,
		}),
		[cloudId, environmentId, environmentType, extensionId, localId, workspaceId],
	);

	const getThreeLOPrompt = useCallback(
		() => ({
			ThreeLOPrompt:
				modalExtension || hasModalConsentPrompt
					? makeModalThreeLOPromptForCustomUI({
							appName: extension.properties.title,
							isBlanketHidden: true,
							onClose: onConsentModalClose,
						})
					: makeThreeLOPromptForCustomUI({
							appName: extension.properties.title,
							onSuccess: onConsentSuccess,
							message: consentMessage,
							onInitialization: onConsentInitialization,
						}),
		}),
		[
			extension.properties.title,
			consentMessage,
			hasModalConsentPrompt,
			modalExtension,
			onConsentSuccess,
			onConsentModalClose,
			onConsentInitialization,
		],
	);

	const customUIProps = {
		accountId,
		bridge,
		client,
		consentMessage,
		contextIds,
		coreData,
		customBridgeMethods,
		dialogs,
		entryPoint,
		extension,
		extensionData,
		extensionPayload,
		extensionViewData,
		getContextToken,
		getThreeLOPrompt,
		height,
		loadingComponent,
		locale,
		localId,
		mentionProvider,
		modalExtension,
		modalStyles,
		modalProvider,
		onConsentModalClose,
		onConsentSuccess,
		onError,
		onForgeDocUpdated,
		onIframeLoad,
		product,
		timezone,
		isIframeHidden,
		isIframeResizable,
		isEmbeddedMacro,
		embeddedParentLocalId,
	};

	const uiKitEmbeddedMacroProps = {
		isEmbeddedMacro,
		embeddedParentLocalId,
	};

	const [viewOnClose, setViewOnClose] = useState<() => Promise<void> | void>();

	// this function has known deps listed already in deps array
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const Frame = useCallback(
		makeFrame((inputProps) => {
			return (
				<CustomUIRenderer
					{...customUIProps}
					extension={{
						...extension,
						properties: { ...extension.properties, ...inputProps },
					}}
					// Ensure extensions that are rendered as entryPoints aren't passing through the very same entryPoint to the Frame
					entryPoint={undefined}
					// Ensures Frame is always resized to the full height of the content
					// Required because Jira leaves isIframeResizable as false
					isIframeResizable={true}
				/>
			);
		}),
		// re-render only depends on extensionData & extensionPayload
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[customUIProps.extensionData, customUIProps.extensionPayload, extension],
	);

	// this function has known deps listed already in deps array
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const FrameResizable = useCallback(
		makeFrame((inputProps) => {
			const { height, width, ...rest } = inputProps;

			// If height or width is provided by app code, the iframe does not automatically resize to contents
			const isIframeResizable = height || width ? false : true;

			return (
				<CustomUIRenderer
					{...customUIProps}
					extension={{
						...extension,
						properties: {
							...extension.properties,
							...(fg('forge-ui-ui-kit-frame-height-width')
								? { resource: inputProps.resource }
								: rest),
						},
					}}
					// Ensure extensions that are rendered as entryPoints aren't passing through the very same entryPoint to the Frame
					entryPoint={undefined}
					// Ensures Frame is always resized to the full height of the content
					// Required because Jira leaves isIframeResizable as false
					isIframeResizable={fg('forge-ui-ui-kit-frame-height-width') ? isIframeResizable : true}
					{...(fg('forge-ui-ui-kit-frame-height-width') ? { height, width } : null)}
				/>
			);
		}),
		// need eslint here as re-render only depends on extensionData & extensionPayload
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[customUIProps.extensionData, customUIProps.extensionPayload, extension],
	);

	// Show loading while fetching fresh extension data for embedded macros
	// Check both loading state and whether results have been received to prevent UI flipping
	if (shouldRefetchExtension && (extensionsLoading || !extensionQueryCompleted)) {
		return getLoadingComponent(loadingComponent);
	}

	// Show error message if embedded macro is not installed on this tenant
	if (extensionNotInstalled) {
		return <ExtensionNotInstalled extensionId={providedExtension.id} />;
	}

	if (fg('forge-ui-add-relay-support')) {
		return (
			<AtlassianRelayEnvironmentProvider>
				<ForgeUIExtensionAnalyticsContext
					extensionId={props.extension.id}
					localId={props.localId || props.extension.id}
				>
					<UFOThirdPartySegment name="forge-ui-app-content-root">
						<PerformanceAnalyticsProviderRollout
							profileKey={`${props.extension.id}-${props.extension.key}-${props.entryPoint}`}
							appType={isCustomUI(props.extension, props.entryPoint) ? 'CustomUI' : 'UIKit'}
							forgeEnvironment={props.extension.environmentType}
							extensionType={props.extension.type}
							appVersion={props.extension.appVersion}
						>
							<PerformanceHoldAttributionAnchorRollout>
								<UserAccessProvider accountId={accountId}>
									{/* hidden span used for testing purposes and to identify that this renderer is being used when toggling the feature flag in products*/}
									<span hidden id="forge-ui-renderer" />
									{!isCustomUI(extension, entryPoint) ? (
										<UIKitRenderer
											coreData={coreData}
											getThreeLOPrompt={getThreeLOPrompt}
											accountId={accountId}
											client={client}
											contextIds={contextIds}
											extension={extension}
											extensionData={extensionData}
											extensionViewData={extensionViewData}
											product={product}
											bridge={{
												// spread down here so product can override the default onClose
												...(modalExtension && fg('forge-ui-on-close-bridge-method')
													? {
															onClose: (callback) => {
																if (extension.environmentType === 'PRODUCTION') {
																	// eslint-disable-next-line no-console
																	console.warn(
																		'Forge view.onClose (EAP) is not available for production apps. The callback will not be registered.',
																	);
																	return false;
																}
																setViewOnClose(() => callback.data);
																return true;
															},
														}
													: {}),
												...bridge,
											}}
											customBridgeMethods={customBridgeMethods}
											components={{
												Frame: fg('ditto-fcf-frame-support-on-issue-create')
													? Frame
													: makeFrame((inputProps) => {
															return (
																<CustomUIRenderer
																	{...customUIProps}
																	extension={{
																		...extension,
																		properties: { ...extension.properties, ...inputProps },
																	}}
																	// Ensure extensions that are rendered as entryPoints aren't passing through the very same entryPoint to the Frame
																	entryPoint={undefined}
																	// Ensures Frame is always resized to the full height of the content
																	// Required because Jira leaves isIframeResizable as false
																	isIframeResizable={true}
																/>
															);
														}),
												...components,
											}}
											consentMessage={consentMessage}
											entryPoint={entryPoint}
											getContextToken={getContextToken}
											locale={locale}
											localId={localId}
											mentionProvider={mentionProvider}
											modalExtension={modalExtension}
											onConsentSuccess={onConsentSuccess}
											onForgeDocUpdated={onForgeDocUpdated}
											timezone={timezone}
											onConsentModalClose={onConsentModalClose}
											onError={onError}
											loadingComponent={loadingComponent}
											extensionPayload={extensionPayload}
											onSuccess={onSuccess}
											{...cloudOrWorkspaceId}
											onAppError={onAppError}
											viewportDimensions={viewportDimensions}
											viewOnClose={viewOnClose}
											{...(fg('platform_add_bridge_message_forwarding_from_faus')
												? uiKitEmbeddedMacroProps
												: {})}
										/>
									) : modalExtension ? (
										// getModalWidth not applied here since all modal extensions are defaulted to medium width
										<ModalCustomUIRenderer
											{...customUIProps}
											bridge={{
												...(modalExtension && fg('forge-ui-on-close-bridge-method')
													? {
															onClose: (callback) => {
																if (extension.environmentType === 'PRODUCTION') {
																	// eslint-disable-next-line no-console
																	console.warn(
																		'Forge view.onClose is not available for production apps during EAP. The callback will not be registered.',
																	);
																	return false;
																}
																setViewOnClose(() => callback.data);
																// stores the callback for when modal is closed
																return true;
															},
														}
													: {}),
												// spread down here so product can override the default onClose
												...customUIProps.bridge,
											}}
											modalOnClose={onModalExtensionClose}
											closeOnEscape={modalExtension?.shouldCloseOnEscapePress ?? true}
											closeOnOverlayClick={modalExtension?.shouldCloseOnOverlayClick ?? true}
											viewOnClose={viewOnClose}
										/>
									) : (
										<CustomUIRenderer
											{...customUIProps}
											getModalWidth={getModalWidth}
											width={width}
										/>
									)}
								</UserAccessProvider>
							</PerformanceHoldAttributionAnchorRollout>
						</PerformanceAnalyticsProviderRollout>
					</UFOThirdPartySegment>
				</ForgeUIExtensionAnalyticsContext>
			</AtlassianRelayEnvironmentProvider>
		);
	}

	// TODO Delete after cleaning up 'forge-ui-add-relay-support' flag
	return (
		<ForgeUIExtensionAnalyticsContext
			extensionId={props.extension.id}
			localId={props.localId || props.extension.id}
		>
			<UFOThirdPartySegment name="forge-ui-app-content-root">
				<PerformanceAnalyticsProviderRollout
					profileKey={`${props.extension.id}-${props.extension.key}-${props.entryPoint}`}
					appType={isCustomUI(props.extension, props.entryPoint) ? 'CustomUI' : 'UIKit'}
					forgeEnvironment={props.extension.environmentType}
					extensionType={props.extension.type}
					appVersion={props.extension.appVersion}
				>
					<PerformanceHoldAttributionAnchorRollout>
						<UserAccessProvider accountId={accountId}>
							{/* hidden span used for testing purposes and to identify that this renderer is being used when toggling the feature flag in products*/}
							<span hidden id="forge-ui-renderer" />
							{!isCustomUI(extension, entryPoint) ? (
								<UIKitRenderer
									coreData={coreData}
									getThreeLOPrompt={getThreeLOPrompt}
									accountId={accountId}
									client={client}
									contextIds={contextIds}
									extension={extension}
									extensionData={extensionData}
									extensionViewData={extensionViewData}
									product={product}
									bridge={
										modalExtension && fg('forge-ui-on-close-bridge-method')
											? {
													onClose: (callback) => {
														if (extension.environmentType === 'PRODUCTION') {
															// eslint-disable-next-line no-console
															console.warn(
																'Forge view.onClose (EAP) is not available for production apps. The callback will not be registered.',
															);
															return false;
														}
														setViewOnClose(() => callback.data);
														return true;
													},
													...bridge,
												}
											: bridge
									}
									customBridgeMethods={customBridgeMethods}
									components={{
										Frame: fg('ditto-fcf-frame-support-on-issue-create')
											? FrameResizable
											: makeFrame((inputProps) => {
													const { height, width, ...rest } = inputProps;

													// If height or width is provided by app code, the iframe does not automatically resize to contents
													const isIframeResizable = height || width ? false : true;

													return (
														<CustomUIRenderer
															{...customUIProps}
															extension={{
																...extension,
																properties: {
																	...extension.properties,
																	...(fg('forge-ui-ui-kit-frame-height-width')
																		? { resource: inputProps.resource }
																		: rest),
																},
															}}
															// Ensure extensions that are rendered as entryPoints aren't passing through the very same entryPoint to the Frame
															entryPoint={undefined}
															// Ensures Frame is always resized to the full height of the content
															// Required because Jira leaves isIframeResizable as false
															isIframeResizable={
																fg('forge-ui-ui-kit-frame-height-width') ? isIframeResizable : true
															}
															{...(fg('forge-ui-ui-kit-frame-height-width')
																? { height, width }
																: null)}
														/>
													);
												}),
										...components,
									}}
									consentMessage={consentMessage}
									entryPoint={entryPoint}
									getContextToken={getContextToken}
									locale={locale}
									localId={localId}
									mentionProvider={mentionProvider}
									modalExtension={modalExtension}
									onConsentSuccess={onConsentSuccess}
									onForgeDocUpdated={onForgeDocUpdated}
									timezone={timezone}
									onConsentModalClose={onConsentModalClose}
									onError={onError}
									loadingComponent={loadingComponent}
									extensionPayload={extensionPayload}
									onSuccess={onSuccess}
									{...cloudOrWorkspaceId}
									onAppError={onAppError}
									viewportDimensions={viewportDimensions}
									viewOnClose={viewOnClose}
									{...(fg('platform_add_bridge_message_forwarding_from_faus')
										? uiKitEmbeddedMacroProps
										: {})}
								/>
							) : modalExtension ? (
								// getModalWidth not applied here since all modal extensions are defaulted to medium width
								<ModalCustomUIRenderer
									{...customUIProps}
									bridge={{
										...(modalExtension && fg('forge-ui-on-close-bridge-method')
											? {
													onClose: (callback) => {
														if (extension.environmentType === 'PRODUCTION') {
															// eslint-disable-next-line no-console
															console.warn(
																'Forge view.onClose is not available for production apps during EAP. The callback will not be registered.',
															);
															return false;
														}
														setViewOnClose(() => callback.data);
														// stores the callback for when modal is closed
														return true;
													},
												}
											: {}),
										// spread down here so product can override the default onClose
										...customUIProps.bridge,
									}}
									modalOnClose={onModalExtensionClose}
									closeOnEscape={modalExtension?.shouldCloseOnEscapePress ?? true}
									closeOnOverlayClick={modalExtension?.shouldCloseOnOverlayClick ?? true}
									viewOnClose={viewOnClose}
								/>
							) : (
								<CustomUIRenderer {...customUIProps} getModalWidth={getModalWidth} width={width} />
							)}
						</UserAccessProvider>
					</PerformanceHoldAttributionAnchorRollout>
				</PerformanceAnalyticsProviderRollout>
			</UFOThirdPartySegment>
		</ForgeUIExtensionAnalyticsContext>
	);
};
